import { formatCurrency, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { agGridDateTimeFilter, agGridDateTimeTableOptions, defaultTableDefOptions } from '../../../helpers';
import { TransactionsService } from '../../../service/transactions.service';
import { GetTransactionsFilters, TransactionItem } from '../../../types';
import { CellSoldDetailsActionTypeComponent } from './cell-sold-details-action-type/cell-sold-details-action-type.component';

@Component({
  selector: 'app-sold-details',
  templateUrl: './sold-details.component.html',
  styleUrls: ['./sold-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SoldDetailsComponent implements OnDestroy {
  private transactionsService = inject(TransactionsService);
  private transactions$: Subscription | null = null;
  public locationId = input.required<number>();

  public soldDetails = signal<TransactionItem[]>([]);
  public isLoading = signal<boolean>(false);
  public readonly minSelectableDate = DateTime.now().minus({ years: 1 }).toJSDate();

  // Maximum date range allowed is 14 days
  public readonly maxDateRangeDays = 14;
  // Default values for first load
  public readonly firstLoadDateRangeDays = 7;
  public readonly today = DateTime.now().toJSDate();
  public readonly defaultFromDate = DateTime.now().minus({ days: this.firstLoadDateRangeDays }).toJSDate();
  minDate = linkedQueryParam<Date>('min_date', {
    parse: (value) => {
      if (value) {
        const parsed = DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate();
        return parsed;
      }
      return this.defaultFromDate;
    },
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });
  maxDate = linkedQueryParam<Date>('max_date', {
    parse: (value) => {
      if (value) {
        const parsed = DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate();
        return parsed;
      }
      return this.today;
    },
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });
  // Ensure user cannot select a range greater than 14 days
  onMinDateChange(date: string | Date) {
    let d: Date;
    if (typeof date === 'string') {
      d = new Date(date);
    } else {
      d = date;
    }
    const maxAllowed = DateTime.fromJSDate(d).plus({ days: this.maxDateRangeDays }).toJSDate();
    if (this.maxDate() > maxAllowed) {
      this.maxDate.set(maxAllowed);
    }
  }
  onMaxDateChange(date: string | Date) {
    let d: Date;
    if (typeof date === 'string') {
      d = new Date(date);
    } else {
      d = date;
    }
    const minAllowed = DateTime.fromJSDate(d).minus({ days: this.maxDateRangeDays }).toJSDate();
    if (this.minDate() < minAllowed) {
      this.minDate.set(minAllowed);
    }
  }

  private transactionsQuery = computed<GetTransactionsFilters | undefined>(() => {
    const minDate = this.minDate();
    const maxDate = this.maxDate();
    const locationId = this.locationId();

    if (!minDate || !maxDate || !locationId) return undefined;

    return {
      from_date: minDate,
      to_date: maxDate,
      location_ids: [locationId],
    };
  });

  public transactionsGrid = viewChild<AgGridAngular<TransactionItem>>('transactionsGrid');

  public soldDetailsCsvFileName = computed(() => {
    const fromDate = DateTime.fromJSDate(this.minDate()).toFormat('yyyy-MM-dd');
    const toDate = DateTime.fromJSDate(this.maxDate()).toFormat('yyyy-MM-dd');

    const locationName = this.soldDetails()[0]?.location_name ?? 'location';

    return `sold_details-${fromDate}-${toDate}-${locationName}.csv`;
  });

  // Converted this from server-side to client-side rendering with a date range
  // limited to a maximum of two weeks, because filtering and pagination were
  // not working correctly. The data was flattened in the backend query,
  // as this was causing frontend pagination issues.
  public readonly tableDef: GridOptions<TransactionItem> = {
    ...defaultTableDefOptions(),
    ...agGridDateTimeTableOptions(),
    rowModelType: 'clientSide',
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [50, 100],
    getRowId: (params) => params.data.item_row_id,
  };

  public readonly columnDefs: ColDef<TransactionItem>[] = [
    {
      field: 'branch_name',
      headerName: 'Branch Name',
      cellDataType: 'text',
      sortable: false,
      hide: true,
    },
    {
      field: 'location_name',
      headerName: 'Location Name',
      cellDataType: 'text',
      sortable: false,
      hide: true,
    },
    {
      field: 'tr_ts',
      headerName: 'Transaction Date',
      cellDataType: 'text',
      sortable: false,
      ...agGridDateTimeFilter(),
      cellRenderer: (params: ICellRendererParams<TransactionItem, string>) => {
        return formatDate(params.value ?? '', 'medium', 'en-US');
      },
      valueFormatter: (params: ValueFormatterParams<TransactionItem, string>) => {
        return DateTime.fromISO(params.value ?? '').toFormat('yyyy-MM-dd HH:mm:ss');
      },
    },
    {
      field: 'image_id',
      headerName: 'Transaction ID',
      cellDataType: 'text',
      sortable: false,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'description',
      headerName: 'Item',
      cellDataType: 'text',
      sortable: false,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'price',
      headerName: 'Item Price',
      cellDataType: 'number',
      sortable: false,
      valueFormatter: (params) => {
        return params.value != null ? formatCurrency(params.value, 'en-US', '$') : '';
      },
      filterValueGetter: (params) => {
        return params.data?.price != null ? formatCurrency(params.data.price, 'en-US', '$') : '';
      },
      filter: 'agTextColumnFilter',
    },
    {
      field: 'total',
      headerName: 'Item Total',
      cellDataType: 'number',
      sortable: false,
      valueFormatter: (params) => {
        return params.value != null ? formatCurrency(params.value, 'en-US', '$') : '';
      },
      filterValueGetter: (params) => {
        return params.data?.total != null ? formatCurrency(params.data.total, 'en-US', '$') : '';
      },
      filter: 'agTextColumnFilter',
    },
    {
      field: 'action_type',
      headerName: 'Action Type',
      cellDataType: 'text',
      sortable: false,
      cellRenderer: CellSoldDetailsActionTypeComponent,
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressMiniFilter: true,
      },
    },
  ];

  constructor() {
    effect(() => {
      const query = this.transactionsQuery();

      if (!query) return;

      this.fetchTransactions(query);
    });
  }

  ngOnDestroy() {
    this.transactions$?.unsubscribe();
    this.transactions$ = null;
  }

  export() {
    this.transactionsGrid()?.api?.exportDataAsCsv({
      fileName: this.soldDetailsCsvFileName(),
      allColumns: true,
    });
  }

  private fetchTransactions(query: GetTransactionsFilters) {
    const rows: TransactionItem[] = [];
    this.transactions$?.unsubscribe();
    this.isLoading.set(true);
    this.soldDetails.set([]);

    this.transactions$ = this.transactionsService
      .streamTransactions(query)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (event) => {
          if (event.type === 'transaction') {
            rows.push({
              ...event.transaction,
              item_row_id: rows.length.toString(),
            });
          }
        },
        complete: () => {
          this.soldDetails.set(rows);
        },
      });
  }
}
